<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Resumen de asistencia</h1>
    </div>

    <!-- Alertas del colaborador seleccionado (Art. 25 D.L. 728) -->
    <div v-if="alerts.length" class="mb-3">
      <div class="flex items-center justify-between mb-2">
        <h6 class="mb-0 font-bold">Alertas — {{ selectedEmployeeName }}</h6>
        <wz-button variant="light" size="sm" @click="clearAlerts">Cerrar</wz-button>
      </div>
      <wz-alert v-for="(a, i) in alerts" :key="i" variant="warning" icon="ri-error-warning-line" class="mb-2">
        {{ a.message }}
      </wz-alert>
    </div>

    <wz-table-container>

      <wz-table-toolbar>
        <template #left>
          <div style="width: 110px; flex-shrink: 0">
            <wz-select v-model="filters.month" :items="monthOptions" @change="loadData" />
          </div>
          <div style="width: 90px; flex-shrink: 0">
            <wz-select v-model="filters.year" :items="yearOptions" @change="loadData" />
          </div>
          <div style="width: 170px; flex-shrink: 0">
            <wz-select :items="companies" :value="filters.companyId" defaultOption="Todas las empresas" @change="onCompanySelected" />
          </div>
          <div style="width: 150px; flex-shrink: 0">
            <wz-select :items="areas" :value="filters.areaId" defaultOption="Todas las áreas" @change="onAreaSelected" />
          </div>
        </template>

        <template #right>
          <wz-tooltip text="Recargar">
            <wz-button variant="outline" @click="loadData">
              <i class="ri-refresh-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-button variant="primary" @click="downloadXlsx" :disabled="downloading" style="white-space: nowrap">
            <i class="ri-file-excel-line mr-1"></i>
            {{ downloading ? 'Descargando...' : 'Descargar Excel' }}
          </wz-button>
        </template>
      </wz-table-toolbar>

      <wz-table>
        <thead>
          <tr>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'employeeName' }" @click="toggleSort('employeeName')">
              Colaborador <i :class="['wz-th-sort-icon', sortIcon('employeeName')]"></i>
            </th>
            <th>DNI</th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'companyName' }" @click="toggleSort('companyName')">
              Empresa <i :class="['wz-th-sort-icon', sortIcon('companyName')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'areaName' }" @click="toggleSort('areaName')">
              Área <i :class="['wz-th-sort-icon', sortIcon('areaName')]"></i>
            </th>
            <th class="text-center wz-th-sortable" :class="{ 'is-sorted': sortKey === 'workableDays' }" @click="toggleSort('workableDays')">
              Días lab. <i :class="['wz-th-sort-icon', sortIcon('workableDays')]"></i>
            </th>
            <th class="text-center wz-th-sortable" :class="{ 'is-sorted': sortKey === 'daysWorked' }" @click="toggleSort('daysWorked')">
              Días trab. <i :class="['wz-th-sort-icon', sortIcon('daysWorked')]"></i>
            </th>
            <th class="text-center">Vacac.</th>
            <th class="text-center">DM</th>
            <th class="text-center">Faltas just.</th>
            <th class="text-center wz-th-sortable" :class="{ 'is-sorted': sortKey === 'unjustifiedAbsences' }" @click="toggleSort('unjustifiedAbsences')">
              Faltas injust. <i :class="['wz-th-sort-icon', sortIcon('unjustifiedAbsences')]"></i>
            </th>
            <th class="text-center">Feriados</th>
            <th class="text-center">Descansos</th>
            <th class="text-center">Permisos</th>
            <th class="text-center wz-th-sortable" :class="{ 'is-sorted': sortKey === 'totalTardinessMinutes' }" @click="toggleSort('totalTardinessMinutes')">
              Min. tardanza <i :class="['wz-th-sort-icon', sortIcon('totalTardinessMinutes')]"></i>
            </th>
            <th class="text-center wz-th-sortable" :class="{ 'is-sorted': sortKey === 'totalOvertimeHours' }" @click="toggleSort('totalOvertimeHours')">
              Hrs extra <i :class="['wz-th-sort-icon', sortIcon('totalOvertimeHours')]"></i>
            </th>
            <th class="text-center wz-th-sortable" :class="{ 'is-sorted': sortKey === 'totalHoursWorked' }" @click="toggleSort('totalHoursWorked')">
              Hrs trabajadas <i :class="['wz-th-sort-icon', sortIcon('totalHoursWorked')]"></i>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="sortedList(rows).length === 0">
            <td colspan="16" class="text-center text-muted py-4">
              No hay datos para el período seleccionado
            </td>
          </tr>
          <tr v-for="(row, idx) in sortedList(rows)" :key="idx"
              :class="{ 'row-selected': selectedRowKey === (row.employeeId + '_' + row.companyId) }">
            <td class="att-name-clickable" @click="selectEmployee(row)" title="Ver alertas de inasistencia">{{ row.employeeName }}</td>
            <td>{{ row.employeeIdentifier }}</td>
            <td>{{ row.companyName }}</td>
            <td>{{ row.areaName }}</td>
            <td class="text-center">{{ row.workableDays }}</td>
            <td class="text-center">{{ row.daysWorked }}</td>
            <td class="text-center">{{ row.vacationDays }}</td>
            <td class="text-center">{{ row.medicalLeaveDays }}</td>
            <td class="text-center">{{ row.justifiedAbsences }}</td>
            <td class="text-center">
              <span :class="row.unjustifiedAbsences > 0 ? 'text-danger font-bold' : ''">
                {{ row.unjustifiedAbsences }}
              </span>
            </td>
            <td class="text-center">{{ row.holidays }}</td>
            <td class="text-center">{{ row.restDays }}</td>
            <td class="text-center">{{ row.permissionDays }}</td>
            <td class="text-center">
              <span :class="row.totalTardinessMinutes > 0 ? 'text-warning font-bold' : ''">
                {{ row.totalTardinessMinutes }}
              </span>
            </td>
            <td class="text-center">
              <span :class="row.totalOvertimeHours > 0 ? 'text-primary font-bold' : ''">
                {{ row.totalOvertimeHours }}
              </span>
            </td>
            <td class="text-center">{{ row.totalHoursWorked }}</td>
          </tr>
        </tbody>
      </wz-table>

    </wz-table-container>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { SortMixin } from '@wizepe/wizeui'
import { AttendanceService, CompanyService, AreaService } from '@/core/services'

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

export default {
  name: 'AttendanceSummary',
  mixins: [SortMixin],

  data() {
    const now = new Date()
    return {
      rows:        [],
      companies:   [],
      areas:       [],
      downloading: false,
      // Alertas de inasistencias del colaborador seleccionado (clic en el nombre)
      selectedEmployeeId: null,
      selectedRowKey: null, // clave compuesta employeeId_companyId de la fila resaltada (multi-empresa)
      selectedEmployeeName: '',
      alerts: [],
      filters: {
        year:      now.getFullYear(),
        month:     now.getMonth() + 1,
        companyId: null,
        areaId:    null,
      },
    }
  },

  computed: {
    monthOptions() {
      return MONTH_NAMES.map((label, idx) => ({ key: idx + 1, label }))
    },
    yearOptions() {
      const current = new Date().getFullYear()
      return Array.from({ length: 5 }, (_, i) => ({ key: current - 2 + i, label: String(current - 2 + i) }))
    },
  },

  methods: {
    async loadData() {
      try {
        this.clearAlerts()
        this.$store.dispatch('app/loading', true)
        const resp = await AttendanceService.getSummary(this.buildPayload())
        this.rows = resp.payload || []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el resumen')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },

    async downloadXlsx() {
      try {
        this.downloading = true
        const resp = await AttendanceService.downloadSummaryXlsx(this.buildPayload())
        const url  = URL.createObjectURL(new Blob([resp.data]))
        const a    = document.createElement('a')
        a.href     = url
        a.download = `asistencia-${this.filters.year}-${String(this.filters.month).padStart(2, '0')}.xlsx`
        a.click()
        URL.revokeObjectURL(url)
      } catch (e) {
        Alert.error('Error al descargar el Excel')
      } finally {
        this.downloading = false
      }
    },

    buildPayload() {
      const p = { year: this.filters.year, month: this.filters.month }
      if (this.filters.companyId) p.companyId = this.filters.companyId
      if (this.filters.areaId)    p.areaId    = this.filters.areaId
      return p
    },

    onCompanySelected(id) {
      this.filters.companyId = (id === null || id === '' || id === 'null' || id === undefined) ? null : id
      this.loadData()
    },
    onAreaSelected(id) {
      this.filters.areaId = (id === null || id === '' || id === 'null' || id === undefined) ? null : id
      this.loadData()
    },

    async selectEmployee(row) {
      this.selectedEmployeeId = row.employeeId
      this.selectedEmployeeName = row.employeeName
      this.selectedRowKey = row.employeeId + '_' + row.companyId
      try {
        const resp = await AttendanceService.getAlerts(row.employeeId, row.companyId)
        this.alerts = resp.payload || []
      } catch (e) {
        this.alerts = []
        Alert.error('Error al cargar las alertas del colaborador')
      }
    },

    clearAlerts() {
      this.alerts = []
      this.selectedEmployeeId = null
      this.selectedEmployeeName = ''
    },
  },

  async created() {
    try {
      const [companiesResp, areasResp] = await Promise.all([
        CompanyService.listAll(),
        AreaService.listAll(),
      ])
      this.companies = (companiesResp.payload || []).map(c => ({ key: c.id, label: c.businessName || c.name }))
      this.areas     = (areasResp.payload     || []).map(a => ({ key: a.id, label: a.description }))
    } catch (e) {
      console.error('Error al cargar catálogos', e)
    }
    this.loadData()
  },
}
</script>

<style lang="stylus">
.wz-table-toolbar-right
  .wz-form-group
    margin-bottom 0

.att-name-clickable
  cursor pointer
  color #0d6efd

  &:hover
    text-decoration underline

tr.row-selected
  background #fff3cd
</style>
